import { useEffect, useRef, useState } from 'react';
import { Animated, Modal, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { colors, radii, shadows, spacing } from '~/ui/tokens';
import { useReducedMotion } from '~/hooks/useReducedMotion';

interface ModalSheetProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'default' | 'wide' | 'deal';
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export function ModalSheet({ visible, onClose, children, size = 'default' }: ModalSheetProps) {
  // Modal unmounts its content the instant `visible` goes false, which would
  // cut the exit animation short — stay mounted through the close animation
  // and only let Modal itself unmount once `progress` has actually reached 0.
  const [mounted, setMounted] = useState(visible);
  const progress = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const reduceMotion = useReducedMotion();
  const { width } = useWindowDimensions();
  // Below the breakpoint this reads as a phone-style bottom sheet, so it
  // slides up from the edge it visually belongs to; wider than that it's a
  // centred dialog, which gets a gentler scale+fade instead.
  const isSheet = width < 700;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      if (reduceMotion) {
        progress.setValue(1);
        return;
      }
      Animated.spring(progress, { toValue: 1, friction: 12, tension: 80, useNativeDriver: true }).start();
      return;
    }
    if (reduceMotion) {
      progress.setValue(0);
      setMounted(false);
      return;
    }
    Animated.timing(progress, { toValue: 0, duration: 200, useNativeDriver: true }).start(({ finished }) => {
      if (finished) setMounted(false);
    });
  }, [visible, reduceMotion, progress]);

  if (!mounted) return null;

  const sheetTransform = isSheet
    ? [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [48, 0] }) }]
    : [{ scale: progress.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] }) }];

  return (
    <Modal
      visible={mounted}
      transparent
      animationType="none"
      presentationStyle="overFullScreen"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <AnimatedPressable style={[styles.backdrop, isSheet && styles.backdropSheet, { opacity: progress }]} onPress={onClose}>
        <AnimatedPressable
          style={[
            styles.sheet,
            size === 'wide' && styles.sheetWide,
            size === 'deal' && styles.sheetDeal,
            isSheet && styles.sheetMobile,
            { transform: sheetTransform },
          ]}
          onPress={(event) => event.stopPropagation()}
        >
          {children}
        </AnimatedPressable>
      </AnimatedPressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    zIndex: 10000,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing(2),
  },
  // Bottom sheet: content sits flush against the bottom edge, not centred.
  backdropSheet: {
    justifyContent: 'flex-end',
    padding: 0,
  },
  sheet: {
    position: 'relative',
    zIndex: 10001,
    backgroundColor: colors.card,
    borderRadius: radii.lg,
    maxWidth: 480,
    width: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    ...shadows.lift,
  },
  sheetMobile: {
    maxWidth: '100%',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  sheetWide: {
    maxWidth: 920,
  },
  sheetDeal: {
    maxWidth: 680,
  },
});
